import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";
import { CaptureActions } from "./CaptureActions";
import { ExperienceSimulationLayout } from "./ExperienceSimulationLayout";
import { SmileSimulationPatientSummary } from "./SmileSimulationPatientSummary";
import { SmileSimulationViewport } from "./SmileSimulationViewport";

type SmileSimulationCapturePageProps = {
  camera: SmileSimulationController["camera"];
  capture: SmileSimulationController["capture"];
  comparison: SmileSimulationController["comparison"];
  isExperience?: boolean;
  language: SmileSimulationLanguage;
  media: SmileSimulationController["media"];
  onEditPatientInfo: SmileSimulationController["actions"]["editCurrentPatientInfo"];
  simulation: SmileSimulationController["simulation"];
  onCapture: SmileSimulationController["actions"]["capture"];
  onSwitchCamera: SmileSimulationController["actions"]["switchCamera"];
  onUploadImage: SmileSimulationController["actions"]["uploadCaptureImage"];
  patient: NonNullable<SmileSimulationController["patient"]["current"]>;
};

export function SmileSimulationCapturePage({
  camera,
  capture,
  comparison,
  isExperience = false,
  language,
  media,
  onEditPatientInfo,
  simulation,
  onCapture,
  onSwitchCamera,
  onUploadImage,
  patient,
}: SmileSimulationCapturePageProps) {
  const copy = getSmileSimulationCopy(language);

  if (isExperience) {
    return (
      <ExperienceSimulationLayout
        title={
          <>
            가장 자연스러운 <span className="text-[#8F14E5]">표정</span>을
            담아주세요!
          </>
        }
        footer={
          <div className="space-y-2 text-center text-[#2b2b2b]">
            <p className="text-4xl font-black leading-none text-[#8F14E5]">!</p>
            <p className="text-[1.15rem] font-extrabold leading-snug sm:text-[1.35rem]">
              평소 웃으시는 것처럼 편안하게 입술을 벌려주시면
              <br />
              더욱 정확한 디자인이 가능해요.
            </p>
          </div>
        }
      >
        <div className="relative h-full w-full">
          <SmileSimulationViewport
            camera={camera}
            capture={capture}
            comparison={comparison}
            isFrameFixed
            language={language}
            media={media}
            step="capture"
            simulation={simulation}
          />
          <div className="absolute inset-x-0 bottom-4 z-20 px-5">
            <CaptureActions
              language={language}
              controls={{
                isCaptureDisabled: capture.isDisabled,
                onCapture,
                onRetake: () => undefined,
                onRetrySimulation: () => undefined,
                isSwitchCameraDisabled:
                  camera.status !== "ready" || !camera.canSwitch,
                onSwitchCamera,
                onUploadImage,
                step: "capture",
                simulationStatus: simulation.status,
              }}
            />
          </div>
        </div>
      </ExperienceSimulationLayout>
    );
  }

  return (
    <section className="flex h-full min-h-0 w-full flex-1 flex-col items-center justify-center gap-4 sm:gap-6">
      <div className="flex w-full items-center justify-end gap-3">
        {!isExperience ? (
          <button
            type="button"
            onClick={onEditPatientInfo}
            className="rounded-full border border-[#8F14E5]/20 bg-white/90 px-4 py-2 text-sm font-semibold text-[#8F14E5] shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition hover:bg-white"
          >
            {copy.capture.editPatient}
          </button>
        ) : null}
      </div>

      {!isExperience ? (
        <SmileSimulationPatientSummary
          caption={copy.capture.currentPatient}
          language={language}
          patient={patient}
        />
      ) : null}

      <SmileSimulationViewport
        camera={camera}
        capture={capture}
        comparison={comparison}
        language={language}
        media={media}
        step="capture"
        simulation={simulation}
      />

      <CaptureActions
        language={language}
        controls={{
          isCaptureDisabled: capture.isDisabled,
          onCapture,
          onRetake: () => undefined,
          onRetrySimulation: () => undefined,
          isSwitchCameraDisabled:
            camera.status !== "ready" || !camera.canSwitch,
          onSwitchCamera,
          onUploadImage,
          step: "capture",
          simulationStatus: simulation.status,
        }}
      />
    </section>
  );
}
